import always from 'ramda/es/always';
import cond from 'ramda/es/cond';
import T from 'ramda/es/T';
import alwaysNull from 'ramda-extension/es/alwaysNull';
import isNilOrEmpty from 'ramda-extension/es/isNilOrEmpty';
import o from 'ramda/es/o';
import between from 'ramda-extension/es/between';
import useWith from 'ramda/es/useWith';
import gte from 'ramda/es/gte';
import lte from 'ramda/es/lte';
import equals from 'ramda/es/equals';
import identity from 'ramda/es/identity';
import length from 'ramda/es/length';
import test from 'ramda/es/test';
import curry from 'ramda/es/curry';
import flip from 'ramda/es/flip';
import modulo from 'ramda/es/modulo';
import compose from 'ramda/es/compose';
import equalsToZero from 'ramda-extension/es/equalsToZero';
import both from 'ramda/es/both';
import either from 'ramda/es/either';
import isNilOrEmptyString from 'ramda-extension/es/isNilOrEmptyString';
import isNumeric from 'ramda-extension/es/isNumeric';
import anyPass from 'ramda/es/anyPass';
import isPositive from 'ramda-extension/es/isPositive';
import ifElse from 'ramda/es/ifElse';
import isTruthy from 'ramda-extension/es/isTruthy';
import replace from 'ramda/es/replace';
import splitAt from 'ramda/es/splitAt';
import reverse from 'ramda/es/reverse';
import pipe from 'ramda/es/pipe';
import map from 'ramda/es/map';
import when from 'ramda/es/when';
import __ from 'ramda/es/__';
import subtract from 'ramda/es/subtract';
import slice from 'ramda/es/slice';
import toString from 'ramda/es/toString';
import concat from 'ramda/es/concat';
import drop from 'ramda/es/drop';
import converge from 'ramda/es/converge';
import invoker from 'ramda/es/invoker';
import isNotNumeric from 'ramda-extension/es/isNotNumeric';
import ltThanLength from 'ramda-extension/es/ltThanLength';
import listToString from 'ramda-extension/es/listToString';
import startsWith$2 from 'ramda/es/startsWith';

/**
 * Creates validation function with predicate and message.
 * Results of validation is ready for translation by react-intl.
 * Result is valid if nil or empty is passed. Use required validation if you want to ensure that field is required.
 *
 * @param {Function} fn validation predicate.
 * @param {String} react intl message (eg. { id: 1, defaultMessage: '' })
 * @param {Object} params for intl message (eg. { min: 1, max: 2 })
 * @return {Object|null} Message object when fails { message, messageValues } or null if pass
 * @example
    > const hasLength = length => createValidation(hasLength(length), m.hasLength, { length })
	> hasLength(6)('abcdef')
	  null
 *
 * @alias module:core.createValidation
 */

var createValidation = (function (predicate, message, messageValues) {
  return cond([[isNilOrEmpty, alwaysNull], // null-safe before predicate
  [predicate, alwaysNull], [T, always({
    message: message,
    messageValues: messageValues
  })]]);
});

var getAge = function getAge(dateString) {
  var today = new Date(Date.now());
  var birthDate = new Date(dateString);
  var age = today.getFullYear() - birthDate.getFullYear();
  var monthDifference = today.getMonth() - birthDate.getMonth();

  if (monthDifference < 0 || monthDifference === 0 && today.getDate() < birthDate.getDate()) {
    return age - 1;
  }

  return age;
};
/**
@alias module:predicates.hasAgeInInterval
*/


var hasAgeInInterval = (function (minAge, maxAge) {
  return o(between(minAge, maxAge), getAge);
});

var m = {
  isTrimmedRight: {
    id: 'validarium.isTrimmedRight',
    defaultMessage: 'isTrimmedRight'
  },
  isTrimmedLeft: {
    id: 'validarium.isTrimmedLeft',
    defaultMessage: 'Must not start tab or space'
  },
  isTrimmed: {
    id: 'validarium.isTrimmed',
    defaultMessage: 'Must not start or end with tab or space'
  },
  isDivisibleBy: {
    id: 'validarium.isDivisibleBy',
    defaultMessage: 'Must be divisible by {divider}'
  },
  isEmail: {
    id: 'validarium.isEmail',
    defaultMessage: 'Invalid e-mail address'
  },
  isInteger: {
    id: 'validarium.isInteger',
    defaultMessage: 'Invalid integer'
  },
  hasLengthInInterval: {
    id: 'validarium.hasLengthInInterval',
    defaultMessage: 'Invalid number of characters ({min} to {max})'
  },
  hasValueInInterval: {
    id: 'validarium.hasValueInInterval',
    defaultMessage: 'Must be between {min} and {max}'
  },
  isNumber: {
    id: 'validarium.isNumber',
    defaultMessage: 'Must be a number'
  },
  isPhoneNumber: {
    id: 'validarium.isPhoneNumber',
    defaultMessage: 'Invalid phone number'
  },
  isPositiveNumber: {
    id: 'validarium.isPositiveNumber',
    defaultMessage: 'Must be a positive number'
  },
  isRequired: {
    id: 'validarium.isRequired',
    defaultMessage: 'This field is required'
  },
  hasLengthMax: {
    id: 'validarium.hasLengthMax',
    defaultMessage: 'Maximum number of characters exceeded ({max})'
  },
  hasLengthMin: {
    id: 'validarium.hasLengthMin',
    defaultMessage: 'Minimum number of characters not reached ({min})'
  },
  hasValueMin: {
    id: 'validarium.hasValueMin',
    defaultMessage: 'Minimum value is {min}'
  },
  hasValueMax: {
    id: 'validarium.hasValueMax',
    defaultMessage: 'Maximum value is {max}'
  },
  hasOnlyDigits: {
    id: 'validarium.hasOnlyDigits',
    defaultMessage: 'Must only contain digits'
  },
  hasNoSpecialSymbols: {
    id: 'validarium.hasNoSpecialSymbols',
    defaultMessage: 'Special characters are not allowed'
  },
  hasLength: {
    id: 'validarium.hasLength',
    defaultMessage: 'Must contain exactly {length} characters'
  },
  startsWith: {
    id: 'validarium.startsWith',
    defaultMessage: 'Value must start with {string}'
  },
  isValidIban: {
    id: 'validarium.isValidIban',
    defaultMessage: 'IBAN is not valid'
  },
  hasAgeInInterval: {
    id: 'validarium.hasAgeInInterval',
    defaultMessage: 'You must be between {minAge} and {maxAge} years old'
  },
  isString: {
    id: 'validarium.isString',
    defaultMessage: 'The value has to be a character string'
  },
  matches: {
    id: 'validarium.hasAgeInInterval',
    defaultMessage: 'Value has a wrong format'
  },
  isOneOf: {
    id: 'validarium.isOneOf',
    defaultMessage: 'Value has to be one of defined'
  },
  isNotOneOf: {
    id: 'validarium.isNotOneOf',
    defaultMessage: "Value can't be any of defined"
  },
  isNegativeNumber: {
    id: 'validarium.isNegativeNumber',
    defaultMessage: 'Value has to be a negative number'
  },
  hasDateMin: {
    id: 'validarium.hasDateMin',
    defaultMessage: 'Date has to be after {min}'
  },
  hasDateMax: {
    id: 'validarium.hasDateMax',
    defaultMessage: 'Date has to be before {max}'
  },
  hasNoWhiteSpace: {
    id: 'validarium.hasNoWhiteSpace',
    defaultMessage: 'Do not use spaces or tabs'
  },
  isRequiredNumber: {
    id: 'validarium.isRequiredNumber',
    defaultMessage: 'This field is required'
  }
};

/**
Checks if the age is in specified interval
@param {number} - minimal age
@param {number} - maximal age
@returns {Object} {message Object} when predicate fails or null when pass
@category number
@example
> hasAgeInInterval(1, 3)(2)
null

> hasAgeInInterval(1, 3)(3)
null

> hasAgeInInterval(1, 3)(5)
{message Object}

@alias module:validations.hasAgeInInterval
*/

var hasAgeInInterval$1 = (function (minAge, maxAge) {
  return createValidation(hasAgeInInterval(minAge, maxAge), m.hasAgeInInterval, {
    minAge: minAge,
    maxAge: maxAge
  });
});

var deleteHours = function deleteHours(date) {
  return date.setHours(0, 0, 0, 0);
};

var makeDate = function makeDate(date) {
  return new Date(date);
};

var makeDateWithoutHours = o(deleteHours, makeDate);

/**
@alias module:predicates.hasDateMax
*/

var hasDateMax = useWith(gte, [makeDateWithoutHours, makeDateWithoutHours]);

/**
Checks if the the date is maximally the specified value
@param {string} - maximum date
@returns {Object} {message Object} when predicate fails or null when pass
@category string
@example
> hasDateMax('2032-07-01')('2020-07-01')
null

> hasDateMax('2032-07-01')('2032-07-01')
null

> hasDateMax('2032-07-01')('2032-07-02')
{message Object}

@alias module:validations.hasDateMax
*/

var hasDateMax$1 = (function (max) {
  return createValidation(hasDateMax(max), m.hasDateMax, {
    max: max
  });
});

/**
@alias module:predicates.hasDateMin
*/

var hasDateMin = useWith(lte, [makeDateWithoutHours, makeDateWithoutHours]);

/**
Checks if the the date is minimally the specified value
@param {string} - minimal date
@returns {Object} {message Object} when predicate fails or null when pass
@category string
@example
> hasDateMin('2032-07-01')('2040-07-01')
null

> hasDateMin('2032-07-01')('2032-07-01')
null

> hasDateMin('2032-07-02')('2032-07-01')
{message Object}

@alias module:validations.hasDateMin
*/

var hasDateMin$1 = (function (min) {
  return createValidation(hasDateMin(min), m.hasDateMin, {
    min: min
  });
});

/**
@alias module:predicates.hasLength
*/

var hasLength = useWith(equals, [identity, length]);

/**
Checks if the value has exact length
@param {number} - desired length for value
@returns {Object} {message Object} when predicate fails or null when pass
@category string
@example
> hasLength(6)('abcdef')
null

> hasLength(6)('abc')
{message Object}

@alias module:validations.hasLength
*/

var hasLength$1 = (function (length) {
  return createValidation(hasLength(length), m.hasLength, {
    length: length
  });
});

/**
@alias module:predicates.hasLengthInterval
*/

var hasLengthInInterval = (function (min, max) {
  return o(between(min, max), length);
});

/**
Checks if the value has length in interval
@param {number} - lower interval endpoint
@param {number} - upper interval endpoint
@returns {Object} {message Object} when predicate fails or null when pass
@category string
@example
> hasLengthInInterval(2, 6)('abcd')
null

> hasLengthInInterval(2, 6)('abcdef')
null

> hasLengthInInterval(2, 6)('a')
{message Object}

@alias module:validations.hasLengthInInterval
*/

var hasLengthInInterval$1 = (function (min, max) {
  return createValidation(hasLengthInInterval(min, max), m.hasLengthInInterval, {
    max: max,
    min: min
  });
});

/**
@alias module:predicates.hasLengthMax
*/

var hasLengthMax = useWith(gte, [identity, length]);

/**
Checks if the values length is lower or equal to max
@param {number} - maximum value
@returns {Object} {message Object} when predicate fails or null when pass
@category string
@example
> hasLengthMax(2)('a')
null

> hasLengthMax(2)('ab')
null

> hasLengthMax(2)('abc')
{message Object}

@alias module:validations.hasLengthMax
*/

var hasLengthMax$1 = (function (max) {
  return createValidation(hasLengthMax(max), m.hasLengthMax, {
    max: max
  });
});

/**
@alias module:predicates.hasLengthMin
*/

var hasLengthMin = useWith(lte, [identity, length]);

/**
Checks if the values length is higher or equal to min
@param {number} - minimum value
@returns {Object} {message Object} when predicate fails or null when pass
@category string
@example
> hasLengthMin(2)('abc')
null

> hasLengthMin(2)('ab')
null

> hasLengthMin(2)('a')
{message Object}

@alias module:validations.hasLengthMin
*/

var hasLengthMin$1 = (function (min) {
  return createValidation(hasLengthMin(min), m.hasLengthMin, {
    min: min
  });
});

/**
@alias module:predicates.hasNoSpecialSymbols
*/

var hasNoSpecialSymbols = test(/^[^_~`!#$%\^&*+=\-\[\]\\;,.\/{}@´'§|\\":<>\?]*$/g);

/**
Checks if the value doesn't contain any special symbol
@returns {Object} {message Object} when predicate fails or null when pass
@category string
@example
> hasNoSpecialSymbols('abc')
null

> hasNoSpecialSymbols('a%b')
{message Object}

@alias module:validations.hasNoSpecialSymbols
*/

var hasNoSpecialSymbols$1 = createValidation(hasNoSpecialSymbols, m.hasNoSpecialSymbols);

/**
@alias module:predicates.hasNoWhiteSpace
*/

var hasNoWhiteSpace = test(/^[\S]*$/g);

/**
Checks if a string contains no white space.
@returns {Object} {message Object} when predicate fails or null when pass
@category number
@example
> hasNoWhiteSpace("")
null

> hasNoWhiteSpace("validarium")
null

> hasNoWhiteSpace("vali darium")
{message Object}

@alias module:validations.hasNoWhiteSpace
*/

var hasNoWhiteSpace$1 = createValidation(hasNoWhiteSpace, m.hasNoWhiteSpace);

/**
@alias module:predicates.hasOnlyDigits
*/

var hasOnlyDigits = test(/^\d*$/);

/**
Checks if the value has only digits
@returns {Object} {message Object} when predicate fails or null when pass
@category string
@example
> hasOnlyDigits('123')
null

> hasOnlyDigits('12n3')
{message Object}

@alias module:validations.hasOnlyDigits
*/

var hasOnlyDigits$1 = createValidation(hasOnlyDigits, m.hasOnlyDigits);

/**
Checks if the value has only digits
@param {number} - lower interval endpoint
@param {number} - upper interval endpoint
@returns {Object} {message Object} when predicate fails or null when pass
@category number
@example
> hasValueInInterval(1, 3)(2)
null

> hasValueInInterval(1, 3)(3)
null

> hasValueInInterval(1, 3)(5)
{message Object}

@alias module:validations.hasValueInInterval
*/

var hasValueInInterval = (function (min, max) {
  return createValidation(between(min, max), m.hasValueInInterval, {
    max: max,
    min: min
  });
});

/**
Checks if the value is lower or equal to max
@param {number} - maximum value
@returns {Object} {message Object} when predicate fails or null when pass
@category number
@example
> hasValueMax(2)(1)
null

> hasValueMax(2)(2)
null

> hasValueMax(2)(3)
{message Object}

@alias module:validations.hasValueMax
*/

var hasValueMax = (function (max) {
  return createValidation(gte(max), m.hasValueMax, {
    max: max
  });
});

/**
Checks if the value is higher or equal to min
@param {number} - minimum value
@returns {Object} {message Object} when predicate fails or null when pass
@category number
@example
> hasValueMin(2)(3)
null

> hasValueMin(2)(2)
null

> hasValueMin(2)(1)
{message Object}

@alias module:validations.hasValueMin
*/

var hasValueMin = (function (min) {
  return createValidation(lte(min), m.hasValueMin, {
    min: min
  });
});

var isDivisible = compose(equalsToZero, modulo);
/**
@alias module:predicates.isDivisibleBy
*/

var isDivisibleBy = curry(flip(isDivisible));

/**
Checks is value is divisible by desired divisor
@param {number} - divisor
@returns {Object} {message Object} when predicate fails or null when pass

@category number
@example
> isDivisibleBy(5)(10)
null

> isDivisibleBy(6)(10)
{message Object}

@alias module:validations.isDivisibleBy
*/

var isDivisibleBy$1 = (function (divisor) {
  return createValidation(isDivisibleBy(divisor), m.isDivisibleBy, {
    divisor: divisor
  });
});

var emailRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/**
@alias module:predicates.isEmail
*/

var isEmail = either(isNilOrEmptyString, both(test(emailRegExp), hasLengthMax(200)));

/**
Checks if the value is valid email
@returns {Object} {message Object} when predicate fails or null when pass

@category string
@example
> isEmail('email@postemail.em')
null

> isEmail('emailpostemail')
{message Object}

@alias module:validations.isEmail
*/

var isEmail$1 = createValidation(isEmail, m.isEmail);

/**
@alias module:predicates.isInteger
*/

var isInteger = test(/^-?[0-9]+$/);

/**
Checks if the value is an integer
@returns {Object} {message Object} when predicate fails or null when pass

@category number
@example
> isInteger(2)
null

> isInteger(2.1)
{message Object}

@alias module:validations.isInteger
*/

var isInteger$1 = createValidation(isInteger, m.isInteger);

/**
Checks if the value is a number
@returns {Object} {message Object} when predicate fails or null when pass

@category string
@example
> isNumber('3')
null

> isNumber('abc')
{message Object}

@alias module:validations.isNumber
*/

var isNumber = createValidation(isNumeric, m.isNumber);

/**
@alias module:predicates.isPhoneNumber
*/

var isPhoneNumber = anyPass([isNilOrEmptyString, test(/^\d{9}$/)]);

/**
Checks if the value is a valid phone number
@returns {Object} {message Object} when predicate fails or null when pass

@category string
@example
> isPhoneNumber('980123456')
null

> isPhoneNumber('23')
{message Object}

@alias module:validations.visPhoneNumber
*/

var isPhoneNumber$1 = createValidation(isPhoneNumber, m.isPhoneNumber);

/**
Checks if the value is a positive number
@returns {Object} {message Object} when predicate fails or null when pass

@category number
@example
> isPositiveNumber(5)
null

> isPositiveNumber(-5)
{message Object}

> isPositiveNumber(0)
{message Object}

@alias module:validations.isPositiveNumber
*/

var isPositiveNumber = createValidation(isPositive, m.isPositiveNumber);

/**
Checks if the value is present
@returns {Object} {message Object} when predicate fails or null when pass

@category other
@example
> isRequired('abc')
null

> isRequired(null)
{message Object}

@alias module:validations.isRequired
*/

var isRequired = ifElse(isTruthy, alwaysNull, always({
  message: m.isRequired
}));

/**
@alias module:predicates.isRequiredNumber
*/

var isRequiredNumber = anyPass([equalsToZero, isTruthy]);

/**
Checks if the value is present
@returns {Object} {message Object} when predicate fails or null when pass

@category other
@example
> isRequiredNumber(0)
null

> isRequiredNumber(null)
{message Object}
@alias module:validations.isRequiredNumber
*/

var isRequiredNumber$1 = ifElse(isRequiredNumber, alwaysNull, always({
  message: m.isRequiredNumber
}));

/**
@alias module:predicates.isTrimmed
*/

var isTrimmed = test(/^\S([\S\s]*?)\S$|^\S*$/);

/**
Checkes if the string do not starts or ends with whitespaces.

@returns {Object} {message Object} when predicate fails or null when pass

@category string
@example
> isTrimmed('valid value')
null

> isTrimmed(' invalid value')
{message Object}

> isTrimmed('invalid value ')
{message Object}

> isTrimmed(' invalid value ')
{message Object}
@alias module:validations.isTrimmed
*/

var isTrimmed$1 = createValidation(isTrimmed, m.isTrimmed);

var isTrimmedLeft = test(/^\S|^\S*$/);

/**
Checkes if the string do not starts with whitespaces.

@returns {Object} {message Object} when predicate fails or null when pass

@category string
@example
> isTrimmedLeft('valid value')
null

> isTrimmedLeft(' invalid value')
{message Object}

@alias module:validations.isTrimmedLeft
*/

var isTrimmedLeft$1 = createValidation(isTrimmedLeft, m.isTrimmedLeft);

var isTrimmedRight = test(/\S$|^\S*$/);

/**
Checkes if the string do not ends with whitespaces.

@returns {Object} {message Object} when predicate fails or null when pass

@category string
@example
> isTrimmedRight('valid value')
null

> isTrimmedRight('invalid value  ')
{message Object}

@alias module:validations.isTrimmedRight
*/

var isTrimmedRight$1 = createValidation(isTrimmedRight, m.isTrimmedRight);

var minus55 = subtract(__, 55);
var decodeChar = o(minus55, invoker(0, 'charCodeAt'));
var removeWhiteSpaces = replace(/ /g, '');
var modulo97 = o(modulo(__, 97), parseInt);
var take9chars = slice(0, 9);
var drop9charsFromIban = converge(drop, [o(length, take9chars), identity]);
var moduloOfFirst9Chars = compose(toString, modulo97, take9chars);
var computeReducedIban = converge(concat, [moduloOfFirst9Chars, drop9charsFromIban]);
var moveFirstFourCharsToEnd = compose(listToString, reverse, splitAt(4)); // on wikipedia they are using hardcoded values, but the value of number can be computed with ascii code - 55

var expandTextCharToNumber = o(listToString, map(when(isNotNumeric, decodeChar))); // javascript is not capable to compute module on whole number
// https://en.wikipedia.org/wiki/International_Bank_Account_Number#Modulo_operation_on_IBAN

var iso7064Mod97 = ifElse(ltThanLength(2), o(function (c) {
  return iso7064Mod97(c);
}, computeReducedIban), modulo97); // based on https://en.wikipedia.org/wiki/International_Bank_Account_Number#Validating_the_IBAN

/**
@alias module:predicates.isValidIban
*/

var isValidIban = pipe(removeWhiteSpaces, moveFirstFourCharsToEnd, expandTextCharToNumber, iso7064Mod97, equals(1));

/**
Checks if the value is a valid IBAN
@returns {Object} {message Object} when predicate fails or null when pass

@category string
@example
> isValidIban('CH4217423156868474686')
null

> isValidIban('CZ123')
{message Object}

@alias module:validations.isValidIban
*/

var isValidIban$1 = createValidation(isValidIban, m.isValidIban);

/**
@alias module:predicates.startsWith
*/

var startsWith = (function (string) {
  return startsWith$2(string);
});

/**
Checks if the value starts with specific string
@returns {Object} {message Object} when predicate fails or null when pass

@category string
@example
> startsWith('dog')('dogo')
null

> startsWith('dog')('cato')
{message Object}

@alias module:validations.startsWith
*/

var startsWith$1 = (function (string) {
  return createValidation(startsWith(string), m.startsWith, {
    string: string
  });
});

export { hasAgeInInterval$1 as hasAgeInInterval, hasDateMax$1 as hasDateMax, hasDateMin$1 as hasDateMin, hasLength$1 as hasLength, hasLengthInInterval$1 as hasLengthInInterval, hasLengthMax$1 as hasLengthMax, hasLengthMin$1 as hasLengthMin, hasNoSpecialSymbols$1 as hasNoSpecialSymbols, hasNoWhiteSpace$1 as hasNoWhiteSpace, hasOnlyDigits$1 as hasOnlyDigits, hasValueInInterval, hasValueMax, hasValueMin, isDivisibleBy$1 as isDivisibleBy, isEmail$1 as isEmail, isInteger$1 as isInteger, isNumber, isPhoneNumber$1 as isPhoneNumber, isPositiveNumber, isRequired, isRequiredNumber$1 as isRequiredNumber, isTrimmed$1 as isTrimmed, isTrimmedLeft$1 as isTrimmedLeft, isTrimmedRight$1 as isTrimmedRight, isValidIban$1 as isValidIban, m as messages, startsWith$1 as startsWith };
